import { useEffect, useMemo, useState } from 'react';
import { normalizeFuelSettings, resolveFuelDiscount, type FuelType } from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import {
  subscribeFuelPrices,
  subscribeFuelSettings,
  type FuelPricesDoc,
  type FuelSettingsDoc,
} from '../lib/repositories/fuelPrices.js';

/**
 * F-11: 店頭掲示用ポスター（A4 縦 1040×1470）。
 *
 * 旧 `as-takefu21ss/DW-price.html` を React 化したもの。背景画像 `back.jpg` の
 * 上に各燃料価格を `position: absolute` で重ねる構造。CSS 値は旧実装の
 * ピクセル位置をそのまま流用（背景画像とのアラインメント維持）。
 *
 * 価格は `subscribeFuelPrices` で onSnapshot 購読。admin が tenant-fuel UI で
 * 価格を更新すると数秒以内にこのページにも反映される（再 deploy 不要）。
 *
 * F-16: キャッシュレス値引き幅（旧 -3 固定）は fuel_settings に連動する。
 * LINE 段階別オファー (price9〜price12) は旧実装と同じく静的値（3 / 4 / 5 / 6・ポスター柄の一部）。
 *
 * ボタン:
 *   - 「印刷」ボタンで window.print() を呼ぶ
 *   - 印刷時は `@media print` でボタンを非表示にする
 *
 * S-136: 画面上の操作部（印刷ボタン / 警告）だけ v2（`ui-v2` を要素ごとに付ける）。ポスター本体は
 * 印刷物なので書体・字間・色を変えない（App.tsx でも `UiV2Scope` に包まない）。v2 の面は
 * すべて `.no-print` の中にあり、印刷時にインクを使わない。
 */

const TARGET_W = 1040;
const TARGET_H = 1470;

/**
 * 旧 DW-price.html と同じ top 値を基本としつつ、ブラウザ間のフォント描画差や
 * 印刷時の見映え調整のため全 12 価格要素に共通で適用する Y 方向オフセット (px)。
 * 値を増やすと全数字が下にズレる。0 で旧版と同等のレイアウト。
 */
const BASE_Y_OFFSET = 10;

const STATIC_LINE = {
  price9: 3,
  price10: 4,
  price11: 5,
  price12: 6,
} as const;

function PriceText({
  className,
  value,
  style,
}: {
  className: string;
  value: number | string;
  style: React.CSSProperties;
}) {
  // 旧 DW-price.html は <p> のブラウザデフォルト `margin: 1em 0` + `line-height: normal`
  // を前提にしたピクセル位置で `top: ...` を指定している。Tailwind preflight は
  //   - <p> の margin を 0 リセット
  //   - body の line-height を 1.5 設定（継承）
  // するため、両方を inline style で復元しないと文字のベースラインが旧版とズレる。
  return (
    <p className={className} style={{ margin: '1em 0', lineHeight: 1.15, ...style }}>
      {value}
    </p>
  );
}

export default function DwPricePage() {
  const { tenantId } = useAuth();
  const [data, setData] = useState<FuelPricesDoc | null>(null);
  const [settingsDoc, setSettingsDoc] = useState<FuelSettingsDoc | null>(null);
  const [settingsError, setSettingsError] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    const u1 = subscribeFuelPrices(tenantId, setData);
    // F-16: キャッシュレス値引き幅を設定に連動させる。読取失敗時は最後に取得できた設定を
    // 保持して警告のみ出す（無警告でデフォルト -3 の掲示物が出るのを防ぐ / レビュー #4・#10）
    const u2 = subscribeFuelSettings(tenantId, (doc, error) => {
      if (error) {
        setSettingsError(true);
        return;
      }
      setSettingsError(false);
      setSettingsDoc(doc);
    });
    return () => {
      u1();
      u2();
    };
  }, [tenantId]);

  const { pricing } = useMemo(() => normalizeFuelSettings(settingsDoc), [settingsDoc]);
  // F-16b: キャッシュレス値引きは燃料別モードもあり得るため燃料ごとに解決する
  const cashlessOffset = (f: FuelType) => -resolveFuelDiscount(pricing.cashless, f);

  // 各燃料 base 値（未ロード時は "--" で「読み込み中」を表す）
  const reguler = data?.regular ?? null;
  const haioku = data?.haioku ?? null;
  const keiyu = data?.keiyu ?? null;
  const touyu = data?.touyu ?? null;

  const fmt = (v: number | null, offset = 0): string =>
    v == null ? '--' : String(v + offset);

  if (!tenantId) {
    return (
      <main className="ui-v2 p-10 text-body text-ink-2">
        <p>tenantId 不明: ログインしてください。</p>
      </main>
    );
  }

  return (
    <main>
      {/* @media print でボタン非表示 + 余白除去 + #root の height: 100% を解除 */}
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff;
            width: 100%;
            height: auto;
            min-height: 0;
          }
          .no-print { display: none !important; }
          .dw-poster {
            box-shadow: none !important;
            margin: 0 !important;
          }
        }
      `}</style>

      <div className="ui-v2 no-print fixed top-3 right-3 z-[1000] flex gap-2">
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center justify-center rounded-lg bg-brand px-5 py-2 text-body font-medium text-white hover:opacity-90"
        >
          印刷
        </button>
      </div>

      {settingsError && (
        <div
          className="ui-v2 no-print mx-auto mt-4 -mb-1 rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink"
          role="alert"
          style={{ width: TARGET_W }}
        >
          設定の取得に失敗しました。キャッシュレス値引き幅が最新でない可能性があります。ページを再読み込みしてから印刷してください。
        </div>
      )}

      <div
        className="dw-poster"
        style={{
          backgroundImage: 'url(/poster/back.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${TARGET_W}px ${TARGET_H}px`,
          width: TARGET_W,
          height: TARGET_H,
          position: 'relative',
          fontFamily: '"Roboto", "Arial Black", sans-serif',
          fontWeight: 900,
          letterSpacing: '-0.2em',
          margin: '20px auto',
          boxShadow: '0 6px 24px rgba(0,0,0,.14)',
        }}
      >
        {/* レギュラー: price1 = base / price2 = base-キャッシュレス値引き（F-16 設定連動） */}
        <div
          style={{
            color: '#ce2800',
            WebkitTextStroke: '9px #ffffff',
            paintOrder: 'stroke',
            textShadow: '6px 6px 0px #ffffff',
          }}
        >
          <PriceText
            className="price1"
            value={fmt(reguler)}
            style={{
              position: 'absolute',
              right: 346,
              top: -52 + BASE_Y_OFFSET,
              fontSize: 74,
            }}
          />
          <PriceText
            className="price2"
            value={fmt(reguler, cashlessOffset('regular'))}
            style={{
              position: 'absolute',
              right: 317,
              top: 15 + BASE_Y_OFFSET,
              fontSize: 132,
            }}
          />
        </div>

        {/* ハイオク: price3 = base-キャッシュレス値引き / price4 = base */}
        <div
          style={{
            color: '#e66000',
            WebkitTextStroke: '10px #ffffff',
            paintOrder: 'stroke',
          }}
        >
          <PriceText
            className="price3"
            value={fmt(haioku, cashlessOffset('haioku'))}
            style={{
              position: 'absolute',
              right: 875,
              top: 500 + BASE_Y_OFFSET,
              fontSize: 77,
            }}
          />
          <PriceText
            className="price4"
            value={fmt(haioku)}
            style={{
              position: 'absolute',
              right: 830,
              top: 642 + BASE_Y_OFFSET,
              fontSize: 54,
            }}
          />
        </div>

        {/* 軽油: price5 = base-キャッシュレス値引き / price6 = base */}
        <div
          style={{
            color: '#019413',
            WebkitTextStroke: '10px #ffffff',
            paintOrder: 'stroke',
          }}
        >
          <PriceText
            className="price5"
            value={fmt(keiyu, cashlessOffset('keiyu'))}
            style={{
              position: 'absolute',
              right: 526,
              top: 500 + BASE_Y_OFFSET,
              fontSize: 77,
            }}
          />
          <PriceText
            className="price6"
            value={fmt(keiyu)}
            style={{
              position: 'absolute',
              right: 485,
              top: 642 + BASE_Y_OFFSET,
              fontSize: 54,
            }}
          />
        </div>

        {/* 灯油: price7 = base-キャッシュレス値引き / price8 = base */}
        <div
          style={{
            color: '#007bc7',
            WebkitTextStroke: '10px #ffffff',
            paintOrder: 'stroke',
          }}
        >
          <PriceText
            className="price7"
            value={fmt(touyu, cashlessOffset('touyu'))}
            style={{
              position: 'absolute',
              right: 183,
              top: 500 + BASE_Y_OFFSET,
              fontSize: 77,
            }}
          />
          <PriceText
            className="price8"
            value={fmt(touyu)}
            style={{
              position: 'absolute',
              right: 130,
              top: 642 + BASE_Y_OFFSET,
              fontSize: 54,
            }}
          />
        </div>

        {/* LINE 段階別: 静的値 3 / 4 / 5 / 6（旧実装踏襲） */}
        <div
          style={{
            color: '#cc2500',
            WebkitTextStroke: '10px #ffffff',
            paintOrder: 'stroke',
            fontStyle: 'italic',
          }}
        >
          <PriceText
            className="price9"
            value={STATIC_LINE.price9}
            style={{
              position: 'absolute',
              right: 860,
              top: 991 + BASE_Y_OFFSET,
              fontSize: 150,
            }}
          />
          <PriceText
            className="price10"
            value={STATIC_LINE.price10}
            style={{
              position: 'absolute',
              right: 238,
              top: 984 + BASE_Y_OFFSET,
              fontSize: 61,
            }}
          />
          <PriceText
            className="price11"
            value={STATIC_LINE.price11}
            style={{
              position: 'absolute',
              right: 238,
              top: 1091 + BASE_Y_OFFSET,
              fontSize: 61,
            }}
          />
          <PriceText
            className="price12"
            value={STATIC_LINE.price12}
            style={{
              position: 'absolute',
              right: 238,
              top: 1200 + BASE_Y_OFFSET,
              fontSize: 61,
            }}
          />
        </div>
      </div>
    </main>
  );
}
